import { useEffect, useState } from 'react';
import PasswordRule from './PasswordRule';

export type RuleNames = 'minLength' | 'capital' | 'lowercase' | 'number' | 'specialChar';

export interface CustomIconComponents {
    validIcon: React.ReactNode;
    invalidIcon: React.ReactNode;
}

type RuleDefinitions = {
    [key in RuleNames]: { valid: boolean; message: string };
};

/**
 * Base properties of the PasswordCheckList component
 *
 * @interface PasswordCheckListBaseProps
 * @member {string} value Current potential password
 * @member {string} valueAgain Current potential password confirmation
 * @member {number} minLength Minimum password length
 * @member {Function} onChange Callback that is triggered when the password becomes valid or invalid across all rules.
 * @member {Object} messages Object with keys as rules, and values as strings to use as the message to be displayed
 * @member {Object} iconComponents Object containing keys of validIcon e invalidIcon with a React Node to be displayed in place of icon.
 */

interface PasswordCheckListBaseProps {
    value: string;
    valueAgain: string;
    minLength: number;
    onChange: (isValid: boolean) => void;
    messages: {
        [key in RuleNames]: string;
    };
    iconComponents: CustomIconComponents;
}

/**
 * PasswordCheckList component properties
 * @interface PasswordCheckListProps
 * @member {RuleNames[]} rules Rules to display in the order desired.
 */

export interface PasswordCheckListProps extends PasswordCheckListBaseProps {
    rules: RuleNames[];
}

const PasswordCheckList = ({
    value,
    valueAgain,
    minLength,
    onChange,
    messages,
    iconComponents,
    rules,
}: PasswordCheckListProps): JSX.Element => {
    const [isValid, setIsValid] = useState<boolean>(false);

    const ruleDefinitions: RuleDefinitions = {
        minLength: {
            valid: value.length >= minLength,
            message: messages.minLength,
        },
        capital: {
            valid: /[A-Z]/.test(value),
            message: messages.capital,
        },
        lowercase: {
            valid: /[a-z]/.test(value),
            message: messages.lowercase,
        },
        number: {
            valid: /\d/.test(value),
            message: messages.number,
        },
        specialChar: {
            valid: /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value),
            message: messages.specialChar,
        },
    };

    const enabledRules = rules.filter(rule => Boolean(ruleDefinitions[rule]));

    useEffect(() => {
        enabledRules.every(rule => ruleDefinitions[rule].valid)
            ? setIsValid(true)
            : setIsValid(false);
    }, [value, valueAgain]);

    useEffect(() => {
        if (typeof onChange === 'function') {
            onChange(isValid);
        }
    }, [isValid]);

    return (
        <ul className="password-check-list">
            {enabledRules.map(rule => {
                const { message, valid } = ruleDefinitions[rule];
                return (
                    <PasswordRule key={rule} valid={valid} iconComponents={iconComponents}>
                        {message}
                    </PasswordRule>
                );
            })}
        </ul>
    );
};

export default PasswordCheckList;
